import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

export interface UseDetectAutofillMutationProps {
  formRef: RefObject<HTMLFormElement> | null;
  onAutofillDetected?: () => void;
}

const useDetectAutofillMutation = ({ formRef, onAutofillDetected }: UseDetectAutofillMutationProps) => {
  const [autofilled, setAutofilled] = useState(false);

  useEffect(() => {
    if (!formRef?.current || !onAutofillDetected) return;

    const handleMutation = (mutationsList: MutationRecord[]) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          // eslint-disable-next-line unicorn/prefer-spread -- can't spread form elements without a TS '--target' of 'es2015' or higher
          const elements = Array.from(formRef?.current?.elements ?? []);
          const autofilledInputExists = elements?.some(
            (el) => el instanceof HTMLInputElement && (el.matches(':autofill') || el.matches(':-webkit-autofill')),
          );

          if (autofilledInputExists) {
            onAutofillDetected();
            break;
          }
        }
      }
    };

    const observer = new MutationObserver(handleMutation);
    observer.observe(formRef.current, { childList: true, subtree: true, attributes: true });

    return () => observer.disconnect();
  }, [formRef, onAutofillDetected]);

  return [autofilled, setAutofilled];
};

export default useDetectAutofillMutation;
